import React, { createContext, useContext, useState } from 'react';

import { IMultiSelectToggleContext, IProviderProps } from '../@types/ContextProviderRelated';
import { EMultiToggleElements } from '../@types/Enums';

const multiSelectDefaultValues: IMultiSelectToggleContext = {
  selectedItems: [
    EMultiToggleElements.WALL_INNER_LENGTH,
    EMultiToggleElements.ELEMENT_DISTANCES,
    EMultiToggleElements.WINDOW_LENGTH,
    EMultiToggleElements.DOOR_LENGTH,
    EMultiToggleElements.PASSAGE_LENGTH,
    EMultiToggleElements.RADIATOR_LENGTH,
    EMultiToggleElements.ROOM_DETAILS,
    EMultiToggleElements.ANGLES,
    EMultiToggleElements.ROOM_COMPONENT_DETAILS,
    EMultiToggleElements.ELEMENT_DETAILS,
    EMultiToggleElements.MEASUREMENTS,
    EMultiToggleElements.GENERAL_INFO,
    EMultiToggleElements.CAMERA_POSITION_SPRITE,
    EMultiToggleElements.HOTSPOTS,
  ],
  saveSelectedItems: () => {},
  showWallInnerLength: true,
  setShowWallInnerLength: () => {},
  showWallOuterLength: false,
  setShowWallOuterLength: () => {},
  showWindowLength: true,
  setShowWindowLength: () => {},
  showDoorLength: true,
  setShowDoorLength: () => {},
  showPassageLength: true,
  setShowPassageLength: () => {},
  showRadiatorLength: true,
  setShowRadiatorLength: () => {},
  showRoomDetails: true,
  setShowRoomDetails: () => {},
  showAngles: true,
  setShowAngles: () => {},
  showRoomComponentDetails: true,
  setShowRoomComponentDetails: () => {},
  showElementDetails: true,
  setShowElementDetails: () => {},
  showMeasurements: true,
  setShowMeasurements: () => {},
  showGeneralInfo: true,
  setShowGeneralInfo: () => {},
  isSnappingEnabled: true,
  setIsSnappingEnabled: () => {},
  showElementDistances: true,
  setShowElementDistances: () => {},
  showCameraPositionSprites: true,
  setShowCameraPositionSprites: () => {},
  showHotspots: true,
  setShowHotspots: () => {},
  temporarilyHideAllElements: async (callback: () => Promise<void> | void) => {
    await callback();
  },
};

// Create the context with default values
export const MultiSelectToggleContext = createContext<IMultiSelectToggleContext>(multiSelectDefaultValues);

export const useMultiSelectToggleContext = () => useContext(MultiSelectToggleContext);

export const MultiSelectToggleProvider = ({ children }: IProviderProps): JSX.Element => {
  const [selectedItems, setSelectedItems] = useState(multiSelectDefaultValues.selectedItems);
  const [showWallInnerLength, setShowWallInnerLength] = useState(multiSelectDefaultValues.showWallInnerLength);
  const [showWallOuterLength, setShowWallOuterLength] = useState(multiSelectDefaultValues.showWallOuterLength);
  const [showWindowLength, setShowWindowLength] = useState(multiSelectDefaultValues.showWindowLength);
  const [showDoorLength, setShowDoorLength] = useState(multiSelectDefaultValues.showDoorLength);
  const [showPassageLength, setShowPassageLength] = useState(multiSelectDefaultValues.showPassageLength);
  const [showRadiatorLength, setShowRadiatorLength] = useState(multiSelectDefaultValues.showRadiatorLength);
  const [showRoomDetails, setShowRoomDetails] = useState(multiSelectDefaultValues.showRoomDetails);
  const [showAngles, setShowAngles] = useState(multiSelectDefaultValues.showAngles);
  const [showRoomComponentDetails, setShowRoomComponentDetails] = useState(multiSelectDefaultValues.showRoomComponentDetails);
  const [showElementDetails, setShowElementDetails] = useState(multiSelectDefaultValues.showElementDetails);
  const [showMeasurements, setShowMeasurements] = useState(multiSelectDefaultValues.showMeasurements);
  const [showGeneralInfo, setShowGeneralInfo] = useState(multiSelectDefaultValues.showGeneralInfo);
  const [isSnappingEnabled, setIsSnappingEnabled] = useState(multiSelectDefaultValues.isSnappingEnabled);
  const [showElementDistances, setShowElementDistances] = useState(multiSelectDefaultValues.showElementDistances);
  const [showCameraPositionSprites, setShowCameraPositionSprites] = useState(multiSelectDefaultValues.showCameraPositionSprites);
  const [showHotspots, setShowHotspots] = useState(multiSelectDefaultValues.showHotspots);

  // Generic handler to update selectedItems based on the current visibility state
  const handleVisibilityToggle = (
    elementId: number,
    currentVisibility: boolean,
    setVisibility: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    const newVisibility = !currentVisibility;
    setVisibility(newVisibility);

    // Update selectedItems based on visibility
    if (newVisibility) {
      setSelectedItems((prevSelected) => [...prevSelected, elementId]);
    } else {
      setSelectedItems((prevSelected) => prevSelected.filter((item) => item !== elementId));
    }
  };

  // helper function that hides all elements, calls a callback, and then restores the states.
  const temporarilyHideAllElements = async <T,>(
    callback: () => Promise<T> | T
  ): Promise<T> => {
    // Save the current visibility flags
    const previousStates = {
      showWallInnerLength,
      showWallOuterLength,
      showWindowLength,
      showDoorLength,
      showPassageLength,
      showRadiatorLength,
      showRoomDetails,
      showAngles,
      showRoomComponentDetails,
      showElementDetails,
      showMeasurements,
      showGeneralInfo,
      showElementDistances,
      showHotspots,
    };
  
    // Hide everything:
    setShowWallInnerLength(false);
    setShowWallOuterLength(false);
    setShowWindowLength(false);
    setShowDoorLength(false);
    setShowPassageLength(false);
    setShowRadiatorLength(false);
    setShowRoomDetails(false);
    setShowAngles(false);
    setShowRoomComponentDetails(false);
    setShowElementDetails(false);
    setShowMeasurements(false);
    setShowGeneralInfo(false);
    setShowElementDistances(false);
    setShowHotspots(false);
  
    let result!: T;
    try {
      result = await callback();
    } finally {
      // Restore the previous flags, even if callback threw:
      setShowWallInnerLength(previousStates.showWallInnerLength);
      setShowWallOuterLength(previousStates.showWallOuterLength);
      setShowWindowLength(previousStates.showWindowLength);
      setShowDoorLength(previousStates.showDoorLength);
      setShowPassageLength(previousStates.showPassageLength);
      setShowRadiatorLength(previousStates.showRadiatorLength);
      setShowRoomDetails(previousStates.showRoomDetails);
      setShowAngles(previousStates.showAngles);
      setShowRoomComponentDetails(previousStates.showRoomComponentDetails);
      setShowElementDetails(previousStates.showElementDetails);
      setShowMeasurements(previousStates.showMeasurements);
      setShowGeneralInfo(previousStates.showGeneralInfo);
      setShowElementDistances(previousStates.showElementDistances);
      setShowHotspots(previousStates.showHotspots);
    }
    return result;
  };


  return (
    <MultiSelectToggleContext.Provider
      value={{
        selectedItems,
        saveSelectedItems: setSelectedItems,
        showWallInnerLength,
        setShowWallInnerLength: () =>
          handleVisibilityToggle(EMultiToggleElements.WALL_INNER_LENGTH, showWallInnerLength, setShowWallInnerLength),
        showWallOuterLength,
        setShowWallOuterLength: () =>
          handleVisibilityToggle(EMultiToggleElements.WALL_OUTER_LENGTH, showWallOuterLength, setShowWallOuterLength),
        showWindowLength,
        setShowWindowLength: () => handleVisibilityToggle(EMultiToggleElements.WINDOW_LENGTH, showWindowLength, setShowWindowLength),
        showDoorLength,
        setShowDoorLength: () => handleVisibilityToggle(EMultiToggleElements.DOOR_LENGTH, showDoorLength, setShowDoorLength),
        showPassageLength,
        setShowPassageLength: () => handleVisibilityToggle(EMultiToggleElements.PASSAGE_LENGTH, showPassageLength, setShowPassageLength),
        showRadiatorLength,
        setShowRadiatorLength: () =>
          handleVisibilityToggle(EMultiToggleElements.RADIATOR_LENGTH, showRadiatorLength, setShowRadiatorLength),
        showRoomDetails,
        setShowRoomDetails: () => handleVisibilityToggle(EMultiToggleElements.ROOM_DETAILS, showRoomDetails, setShowRoomDetails),
        showAngles,
        setShowAngles: () => handleVisibilityToggle(EMultiToggleElements.ANGLES, showAngles, setShowAngles),
        showRoomComponentDetails: showRoomComponentDetails,
        setShowRoomComponentDetails: () =>
          handleVisibilityToggle(EMultiToggleElements.ROOM_COMPONENT_DETAILS, showRoomComponentDetails, setShowRoomComponentDetails),
        showElementDetails,
        setShowElementDetails: () =>
          handleVisibilityToggle(EMultiToggleElements.ELEMENT_DETAILS, showElementDetails, setShowElementDetails),
        showMeasurements,
        setShowMeasurements: () => handleVisibilityToggle(EMultiToggleElements.MEASUREMENTS, showMeasurements, setShowMeasurements),
        showGeneralInfo,
        setShowGeneralInfo: () => handleVisibilityToggle(EMultiToggleElements.GENERAL_INFO, showGeneralInfo, setShowGeneralInfo),
        isSnappingEnabled,
        setIsSnappingEnabled,
        showElementDistances,
        setShowElementDistances: () =>
          handleVisibilityToggle(EMultiToggleElements.ELEMENT_DISTANCES, showElementDistances, setShowElementDistances),
        showCameraPositionSprites,
        setShowCameraPositionSprites: () =>
          handleVisibilityToggle(EMultiToggleElements.CAMERA_POSITION_SPRITE, showCameraPositionSprites, setShowCameraPositionSprites),
        showHotspots,
        setShowHotspots: () => handleVisibilityToggle(EMultiToggleElements.HOTSPOTS, showHotspots, setShowHotspots),
        temporarilyHideAllElements,
      }}
    >
      {children}
    </MultiSelectToggleContext.Provider>
  );
};

export default MultiSelectToggleProvider;
